import { Download, Search } from "lucide-react";
import { Link } from "react-router-dom";
import type { Me } from "../../lib/me";
import { RoleButton } from "../RequireRole";
import { importable } from "./flow";
import { pidUrls, type RegisterTag } from "./pidApi";
import { TableScroll } from "../TableScroll";

const STATUS_TONE: Record<string, string> = { ok: "ok", ambiguous: "review", unparsed: "fail" };

/** The package tag register, a find box, the CSV download and the import button. */
export function RegisterTable({ pkg, tags, all, query, me, busy, note, imported = [], onQuery, onImport, onOpen }: {
  pkg: string;
  /** The rows to show: all of them, or what find returned. */
  tags: RegisterTag[];
  /** Every row of the register, for the import count. */
  all: RegisterTag[];
  query: string;
  me: Me | null | undefined;
  busy: boolean;
  note: string | null;
  /** What the last import put in the register, or found there, each linked to its entry. */
  imported?: { tag: string; href: string; isNew: boolean }[];
  onQuery: (q: string) => void;
  onImport: () => void;
  onOpen: (sheetId: string, readingId: string) => void;
}) {
  const ready = importable(all).length;
  return (
    <div className="stack" style={{ gap: 12 }} data-pid-register>
      <div className="filters" style={{ marginBottom: 0 }}>
        <div className="searchbox" style={{ marginBottom: 0, flex: "1 1 260px" }}>
          <Search aria-hidden="true" />
          <input aria-label="Find a tag" placeholder="Find a tag, for example PT-403" value={query} onChange={(e) => onQuery(e.target.value)} />
        </div>
        <a className="btn" href={pidUrls.csv(pkg)} download
          style={{ background: "transparent", color: "var(--color-text)", border: "1px solid var(--color-border)", textDecoration: "none" }}>
          <span className="row" style={{ gap: 6 }}><Download size={16} aria-hidden="true" /> CSV</span>
        </a>
        <RoleButton role="engineer" me={me} disabled={busy || ready === 0} onClick={onImport}
          title={ready === 0 ? "No reviewed instrument, valve or equipment tags yet" : undefined}>
          {busy ? "Importing" : `Import ${ready} to asset register`}
        </RoleButton>
      </div>
      {note && <p role="status" className="muted">{note}</p>}
      {imported.length > 0 && (
        <nav aria-label="Imported register entries" className="row" style={{ gap: 10, flexWrap: "wrap", fontSize: 13 }}>
          <span className="muted">Open in the register:</span>
          {imported.map((t) => (
            <Link key={t.href} to={t.href} className="mono" title={t.isNew ? "Added by this import" : "Already in the register"}>
              {t.tag}
            </Link>
          ))}
        </nav>
      )}
      <section className="card" style={{ padding: 0, overflow: "hidden" }}>
        <TableScroll label="Tag register">
        <table>
          <thead>
            <tr><th>Tag</th><th>Kind</th><th>Status</th><th>Reviewed</th><th>Where</th></tr>
          </thead>
          <tbody>
            {tags.length === 0 && (
              <tr><td colSpan={5} className="muted">{query ? `No tag matches ${query}.` : "No tags read yet."}</td></tr>
            )}
            {tags.map((t) => {
              const first = t.occurrences[0];
              return (
                <tr key={`${t.tag}|${t.kind}`} data-register-tag={t.tag}>
                  <td className="mono" style={{ color: "var(--color-primary)" }}>
                    {t.tag}{t.from_range && <span className="muted" style={{ fontSize: 11 }}> (from a range)</span>}
                  </td>
                  <td>{t.kind.replace(/_/g, " ")}</td>
                  <td><span className={`pill ${STATUS_TONE[t.status] ?? "info"}`} title={t.reasons.join("; ")}>{t.status}</span></td>
                  <td>{t.reviewed ? "yes" : "no"}</td>
                  <td>
                    {first ? (
                      <button className="ghost" onClick={() => onOpen(first.sheet_id, first.reading_id)}>
                        {first.filename}, page {first.page}{t.occurrences.length > 1 ? ` and ${t.occurrences.length - 1} more` : ""}
                      </button>
                    ) : "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </TableScroll>
      </section>
    </div>
  );
}
